import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Clock,
  CheckCircle,
  XCircle,
  MapPin,
  Phone,
  Mail,
  Globe,
  User,
  Eye,
  RefreshCw,
  AlertCircle,
  Building,
  FileText,
  ExternalLink,
  Shield,
  Hash,
} from 'lucide-react'
import {
  yogaSchoolService,
  YogaSchool,
  formatRelativeTime,
} from '../services/yogaSchoolService'
import toast from 'react-hot-toast'
import AdminPagination from '../components/AdminPagination'

const extractErrorMessage = (err: any, fallback: string): string => {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  )
}

const SchoolRegistrations = () => {
  const navigate = useNavigate()
  const [schools, setSchools] = useState<YogaSchool[]>([])
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [sourceFilter, setSourceFilter] = useState<'ALL' | 'YOGA_SCHOOL' | 'BOOKMYYOGATTC'>('ALL')
  const [docsFilter, setDocsFilter] = useState<'ALL' | 'SUBMITTED' | 'NOT_SUBMITTED'>('ALL')

  // Detail modal
  const [selectedSchool, setSelectedSchool] = useState<YogaSchool | null>(null)

  // Approve confirmation modal
  const [approveSchoolTarget, setApproveSchoolTarget] = useState<YogaSchool | null>(null)

  // Reject modal
  const [rejectSchool, setRejectSchool] = useState<YogaSchool | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await yogaSchoolService.getPendingYogaSchools({
        page: currentPage,
        limit: 10,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        createdBySource: sourceFilter !== 'ALL' ? sourceFilter : undefined,
        verificationDocumentsSubmitted:
          docsFilter === 'SUBMITTED' ? 'true' : docsFilter === 'NOT_SUBMITTED' ? 'false' : undefined,
      })
      if (response.success && response.data) {
        setSchools(response.data.schools)
        const pagination = response.data.pagination
        setTotalPages(pagination?.pages ?? pagination?.totalPages ?? 1)
        setTotal(pagination?.total ?? pagination?.totalItems ?? response.data.schools?.length ?? 0)
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch pending registrations')
    } finally {
      setLoading(false)
    }
  }, [currentPage, sourceFilter, docsFilter])

  useEffect(() => {
    fetchPending()
  }, [fetchPending])

  const handleApproveConfirm = async () => {
    if (!approveSchoolTarget) return
    const target = approveSchoolTarget
    setActionLoading(target._id)
    // Always close the popup so the user isn't stuck behind a modal regardless
    // of whether the API call succeeds or fails.
    setApproveSchoolTarget(null)
    try {
      await yogaSchoolService.approveSchool(target._id)
      setSchools((prev) => prev.filter((s) => s._id !== target._id))
      setTotal((prev) => prev - 1)
      setSelectedSchool(null)
      toast.success(`Approved ${target.name}`)
    } catch (err: any) {
      const message = extractErrorMessage(err, 'Failed to approve yoga school')
      toast.error(message)
      setError(message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleRejectSubmit = async () => {
    if (!rejectSchool) return
    const target = rejectSchool
    const reason = rejectReason
    setActionLoading(target._id)
    // Close the modal in all cases so the user never gets stuck behind it.
    setRejectSchool(null)
    setRejectReason('')
    try {
      await yogaSchoolService.rejectSchool(target._id, reason || undefined)
      setSchools((prev) => prev.filter((s) => s._id !== target._id))
      setTotal((prev) => prev - 1)
      setSelectedSchool(null)
      toast.success(`Rejected ${target.name}`)
    } catch (err: any) {
      const message = extractErrorMessage(err, 'Failed to reject yoga school')
      toast.error(message)
      setError(message)
    } finally {
      setActionLoading(null)
    }
  }

  interface OwnerInfo {
    _id: string
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
  }

  const docTypeLabels: Record<string, string> = {
    businessReg: 'Business Registration',
    gst: 'GST Certificate',
    tradeLicense: 'Trade License / Permit',
    ownerId: 'Owner ID',
    addressProof: 'Address Proof',
    yogaAlliance: 'Yoga Alliance Certificate',
    additional: 'Additional Document',
  }

  const owner = (school: YogaSchool): OwnerInfo | null => {
    if (!school.ownerId || typeof school.ownerId === 'string') return null
    return school.ownerId as OwnerInfo
  }

  const hasListFilters = sourceFilter !== 'ALL' || docsFilter !== 'ALL'

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">New Registrations</h1>
            <p className="text-sm text-gray-500 mt-1">
              {total} school{total !== 1 ? 's' : ''} pending approval
              {hasListFilters ? ' (filtered)' : ''}
            </p>
          </div>
          <button
            onClick={fetchPending}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col sm:flex-row flex-wrap gap-3 sm:items-center">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide sm:mr-1">Filters</span>
          <select
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value as typeof sourceFilter)
              setCurrentPage(1)
            }}
            className="w-full sm:w-56 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
          >
            <option value="ALL">All sources</option>
            <option value="BOOKMYYOGATTC">Bookmyyogattc onboarded</option>
            <option value="YOGA_SCHOOL">School self-registered</option>
          </select>
          <select
            value={docsFilter}
            onChange={(e) => {
              setDocsFilter(e.target.value as typeof docsFilter)
              setCurrentPage(1)
            }}
            className="w-full sm:w-56 px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
          >
            <option value="ALL">Verification docs: any</option>
            <option value="SUBMITTED">Docs submitted</option>
            <option value="NOT_SUBMITTED">Docs not submitted</option>
          </select>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-sm text-red-800">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
              <XCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
          </div>
        ) : schools.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              {hasListFilters ? 'No matches' : 'All Caught Up'}
            </h3>
            <p className="text-sm text-gray-500">
              {hasListFilters
                ? 'No pending registrations match these filters. Try clearing filters or adjusting your selection.'
                : 'No pending school registrations at the moment.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {schools.map((school, index) => {
              const o = owner(school)
              return (
                <motion.div
                  key={school._id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      {/* School info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                            <Clock className="w-3 h-3" />
                            School Approval: Pending
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatRelativeTime(school.createdAt)}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{school.name}</h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" />
                            {school.city && school.country
                              ? `${school.city}, ${school.country}`
                              : school.location || '—'}
                          </span>
                          {o && (
                            <span className="flex items-center gap-1">
                              <User className="w-3.5 h-3.5" />
                              {o.firstName} {o.lastName}
                            </span>
                          )}
                          {o?.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3.5 h-3.5" />
                              {o.email}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => navigate(`/admin/yoga-schools/${school._id}`)}
                          className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setApproveSchoolTarget(school)}
                          disabled={actionLoading === school._id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve School
                        </button>
                        <button
                          onClick={() => setRejectSchool(school)}
                          disabled={actionLoading === school._id}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject School
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </div>
        )}

        <AdminPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={total}
          disabled={loading}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* ========== Detail Modal ========== */}
      <AnimatePresence>
        {selectedSchool && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
            onClick={() => setSelectedSchool(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex items-center justify-between z-10">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedSchool.name}</h2>
                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                    <Clock className="w-3 h-3" />
                    School Approval: Pending
                  </span>
                </div>
                <button
                  onClick={() => setSelectedSchool(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Owner Info */}
                {(() => {
                  const o = owner(selectedSchool)
                  if (!o) return null
                  return (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Registered By
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                        <div>
                          <span className="text-blue-600 font-medium">Name</span>
                          <p className="text-blue-900">{o.firstName} {o.lastName}</p>
                        </div>
                        {o.email && (
                          <div>
                            <span className="text-blue-600 font-medium">Email</span>
                            <p className="text-blue-900">{o.email}</p>
                          </div>
                        )}
                        {o.phone && (
                          <div>
                            <span className="text-blue-600 font-medium">Phone</span>
                            <p className="text-blue-900">{o.phone}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })()}

                {/* School Details */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    School Details
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">School Name</span>
                      <p className="font-medium text-gray-900">{selectedSchool.name}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Slug</span>
                      <p className="font-medium text-gray-900">{selectedSchool.slug || '—'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Location</span>
                      <p className="font-medium text-gray-900">{selectedSchool.location || '—'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">City, Country</span>
                      <p className="font-medium text-gray-900">
                        {[selectedSchool.city, selectedSchool.country].filter(Boolean).join(', ') || '—'}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm mt-4">
                    {selectedSchool.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="font-medium text-gray-900">{selectedSchool.phone}</span>
                      </div>
                    )}
                    {selectedSchool.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="font-medium text-gray-900 truncate">{selectedSchool.email}</span>
                      </div>
                    )}
                    {selectedSchool.website && (
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <a href={selectedSchool.website} target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:underline truncate">
                          {selectedSchool.website}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description */}
                {selectedSchool.description && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Description
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-lg p-3 border border-gray-100">
                      {selectedSchool.description}
                    </p>
                  </div>
                )}

                {/* ===== Verification Documents & Numbers ===== */}
                {(() => {
                  const meta = selectedSchool.verificationMeta || {}
                  const docs = selectedSchool.verificationDocuments || []
                  const hasAnything = Object.values(meta).some(v => v) || docs.length > 0

                  if (!hasAnything) {
                    return (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                        <h4 className="text-sm font-semibold text-red-800 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          No Verification Documents Submitted
                        </h4>
                        <p className="text-xs text-red-600 mt-1">
                          This school has no verification documents or registration numbers on file yet.
                        </p>
                      </div>
                    )
                  }

                  const metaLabels: Record<string, string> = {
                    businessRegNumber: 'Business Reg. No.',
                    gstNumber: 'GST Number',
                    tradeLicense: 'Trade License',
                    ownerIdType: 'Owner ID Type',
                    ownerIdNumber: 'Owner ID Number',
                    yogaAllianceNumber: 'Yoga Alliance Cert.',
                  }

                  return (
                    <div className="space-y-4">
                      {/* Document Numbers */}
                      {Object.values(meta).some(v => v) && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Hash className="w-4 h-4" />
                            Document Numbers
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {Object.entries(meta).map(([key, value]) => {
                              if (!value) return null
                              return (
                                <div key={key} className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                                    {metaLabels[key] || key}
                                  </p>
                                  <p className="text-sm font-medium text-gray-900 mt-0.5">{value}</p>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}

                      {/* Uploaded Files */}
                      {docs.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Shield className="w-4 h-4" />
                            Uploaded Documents ({docs.length})
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {docs.map((doc: string | { url: string; type: string }, idx: number) => {
                              const url = typeof doc === 'string' ? doc : doc.url
                              const docType = typeof doc === 'string' ? 'additional' : (doc.type || 'additional')
                              const label = docTypeLabels[docType] || docType
                              const isPdf = url.split('?')[0].toLowerCase().endsWith('.pdf')
                              return (
                                <a
                                  key={idx}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-amber-400 hover:bg-amber-50 transition-all"
                                >
                                  <div className={`p-2 rounded-lg ${isPdf ? 'bg-red-100' : 'bg-blue-100'}`}>
                                    <FileText className={`w-4 h-4 ${isPdf ? 'text-red-600' : 'text-blue-600'}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 group-hover:text-amber-700">
                                      {label}
                                    </p>
                                    <p className="text-xs text-gray-500">{isPdf ? 'PDF' : 'Image'}</p>
                                  </div>
                                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-amber-600 flex-shrink-0" />
                                </a>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })()}

                {/* Meta */}
                <div className="text-xs text-gray-400 pt-2 border-t border-gray-100">
                  Registered {formatRelativeTime(selectedSchool.createdAt)} &middot; ID: {selectedSchool._id}
                </div>
              </div>

              {/* Actions */}
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-2xl flex items-center justify-end gap-3">
                <button
                  onClick={() => setSelectedSchool(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setRejectSchool(selectedSchool)
                  }}
                  disabled={actionLoading === selectedSchool._id}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                >
                  Reject
                </button>
                <button
                  onClick={() => setApproveSchoolTarget(selectedSchool)}
                  disabled={actionLoading === selectedSchool._id}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  Approve School
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ========== Approve Confirmation Modal ========== */}
      <AnimatePresence>
        {approveSchoolTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
            onClick={() => setApproveSchoolTarget(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Approve School</h3>
                    <p className="text-sm text-gray-500">{approveSchoolTarget.name}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Are you sure you want to approve <span className="font-semibold">{approveSchoolTarget.name}</span>?
                  This will activate the school and all its courses, making them visible to users.
                </p>
              </div>
              <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-2xl flex justify-end gap-3">
                <button
                  onClick={() => setApproveSchoolTarget(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApproveConfirm}
                  disabled={actionLoading === approveSchoolTarget._id}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading === approveSchoolTarget._id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Yes, Approve
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ========== Reject Reason Modal ========== */}
      <AnimatePresence>
        {rejectSchool && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
            onClick={() => {
              setRejectSchool(null)
              setRejectReason('')
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <XCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Reject School</h3>
                    <p className="text-sm text-gray-500">{rejectSchool.name}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to reject <span className="font-semibold">{rejectSchool.name}</span>?
                  This will deactivate the school and all its courses.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for rejection <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-400 transition-all text-sm resize-none"
                    placeholder="e.g. Incomplete documentation, invalid business registration..."
                  />
                  {rejectReason.trim().length === 0 && (
                    <p className="text-xs text-red-500 mt-1">Please provide a reason for rejection.</p>
                  )}
                </div>
              </div>
              <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-2xl flex justify-end gap-3">
                <button
                  onClick={() => {
                    setRejectSchool(null)
                    setRejectReason('')
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectSubmit}
                  disabled={actionLoading === rejectSchool._id || rejectReason.trim().length === 0}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading === rejectSchool._id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  Reject School
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default SchoolRegistrations
